<script setup lang="ts">
    import { watch } from 'vue'
    import { useEditor, EditorContent } from '@tiptap/vue-3'
    import StarterKit from '@tiptap/starter-kit'
    import Underline from '@tiptap/extension-underline'

    const props = defineProps<{
        content: string
    }>()
    const emit = defineEmits<{
        (e: 'update:content', value: string): void
        (e: 'text-changed', value: string): void
    }>()

    const editor = useEditor({
        content: props.content,
        extensions: [StarterKit, Underline],
        onUpdate({ editor }) {
            const html = editor.getHTML()
            emit('update:content', html)
            emit('text-changed', html)
        }
    })

    watch(
        () => props.content,
        (newVal) => {
            if (editor.value && newVal !== editor.value.getHTML()) {
                editor.value.commands.setContent(newVal, false)
            }
        }
    )
</script>

<template>
    <div class="tiptap-editor">
        <div v-if="editor" class="tiptap-toolbar">
            <v-btn-group density="compact" variant="outlined" class="mr-1">
                <v-btn size="small" :active="editor.isActive('bold')" @click="editor.chain().focus().toggleBold().run()">
                    <v-icon>mdi-format-bold</v-icon>
                </v-btn>
                <v-btn size="small" :active="editor.isActive('italic')" @click="editor.chain().focus().toggleItalic().run()">
                    <v-icon>mdi-format-italic</v-icon>
                </v-btn>
                <v-btn size="small" :active="editor.isActive('underline')" @click="editor.chain().focus().toggleUnderline().run()">
                    <v-icon>mdi-format-underline</v-icon>
                </v-btn>
            </v-btn-group>
            <v-btn-group density="compact" variant="outlined" class="mr-1">
                <v-btn size="small" :active="editor.isActive('bulletList')" @click="editor.chain().focus().toggleBulletList().run()">
                    <v-icon>mdi-format-list-bulleted</v-icon>
                </v-btn>
                <v-btn size="small" :active="editor.isActive('orderedList')" @click="editor.chain().focus().toggleOrderedList().run()">
                    <v-icon>mdi-format-list-numbered</v-icon>
                </v-btn>
            </v-btn-group>
            <v-btn-group density="compact" variant="outlined">
                <v-btn size="small" :disabled="!editor.can().undo()" @click="editor.chain().focus().undo().run()">
                    <v-icon>mdi-undo</v-icon>
                </v-btn>
                <v-btn size="small" :disabled="!editor.can().redo()" @click="editor.chain().focus().redo().run()">
                    <v-icon>mdi-redo</v-icon>
                </v-btn>
            </v-btn-group>
        </div>
        <editor-content :editor="editor" class="tiptap-content" />
    </div>
</template>

<style>
    .tiptap-editor {
        border: 1px solid rgba(0, 0, 0, 0.38);
        border-radius: 4px;
        flex-grow: 1;
    }

    .tiptap-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        padding: 6px 8px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
        background: #f5f5f5;
    }

    .tiptap-toolbar .v-btn {
        min-width: 36px;
        padding-inline: 10px;
    }

    .tiptap-content {
        padding: 12px;
        min-height: 120px;
    }

    .tiptap-content .tiptap {
        outline: none;
    }

    .tiptap-content .tiptap ol,
    .tiptap-content .tiptap ul {
        padding-left: 1.5em;
    }

    .tiptap-content .tiptap ol ol,
    .tiptap-content .tiptap ul ul,
    .tiptap-content .tiptap ol ul,
    .tiptap-content .tiptap ul ol {
        margin: 0.25em 0;
    }
</style>
